// eslint:disable max-func-body-length
import * as React from 'react';
import { DefaultContainerProps } from 'app/infrastructure/redux';
import { STATE } from 'app/constants';
import { actionCreators } from '../actions';
import { SerializeType, SimpleFormProps } from 'app/infrastructure/simple-form/types';
import { CreateFolder } from '../create-folder/create-folder';
import { endpoint, formFolder, ReportId, form } from '../constants';
import { FIELD_TYPE, simpleFieldForPath, SimpleFormValidationConfig, withSimpleForm }
  from 'app/infrastructure/simple-form';
import { defaultSerializeMappings } from 'app/infrastructure/simple-form/serialize';
import { shouldClear } from '../selectors';
import { requiredValidation } from 'app/infrastructure/simple-form/validation/rules';
import { CreateReportState, EditFolderProps } from 'app/area/saved-reports/types';
import styles from '../create-report.scss';
import { ElementLoader, Validation, ValidationLevel } from 'ui';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import get from 'lodash/get';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { ModalSize } from 'app/components/constants';

type EditFolderComponentProps = DefaultContainerProps & EditFolderProps & typeof actionCreators
  & DefaultContainerProps & SimpleFormProps;

const SimpleForm = simpleFieldForPath(STATE.savedReport);

class EditFolderComponent extends React.Component<EditFolderComponentProps, CreateReportState> {

  public static statePath: string = STATE.savedReport;

  public componentDidUpdate(prevProps: EditFolderComponentProps) {
    if (shouldClear(prevProps, this.props)) {
      this.onCancel();
    }
  }

  private validationConfig: SimpleFormValidationConfig = [
    requiredValidation(formFolder.title)
  ];

  private onSubmit = () => {
    const { validate, serialize, saveFolderFavourite, definition, translation, reportCategories, folderType } = this.props;
    const originalFavouritId = get(definition, 'validState.validFavourite.v');
    validate(this.validationConfig, () =>
      saveFolderFavourite(
        serialize(defaultSerializeMappings),
        originalFavouritId,
        reportCategories,
        folderType
      ),
      translation.validation_error);
  };

  private onCancel = () => {
    this.props.onClose();
  };

  private hasOwner = (id: number) => {
    const { definition } = this.props;
    const owners = definition.validState?.owners;
    return (owners ?? []).map(owner => owner.value).includes(id);
  };

  private descendantsOwnerNotUpdated = (): boolean => {
    const { definition, sharedState } = this.props;
    const updateOwnerForAllDescendants = definition && get(this.props, 'formValues.updateOwnerForAllDescendants');
    return !this.hasOwner(sharedState.id) ? !updateOwnerForAllDescendants : false;
  };

  private parentOwnerNotUpdated = (): boolean => {
    const { ownerParent } = this.props;
    return !this.hasOwner(ownerParent) ? true : false;
  };

  private renderFields = () => {
    const { definition, sharedState, translation, fetchingCreateFolder, searchRequiredUsers, folderType,
      selectedReport, hasDescendants, ...rest } = this.props;
    const validState = definition.validState;
    const textUpdateOwners = selectedReport === ReportId.Dashboard ? translation.update_owners_dashboard : translation.update_owners;
    const hasParent = !!definition?.validState?.parentFavourite;

    return <div>
      <div className="row">
        <div className="col-lg-4">
          <SimpleForm
            name={formFolder.title}
            maxLength={254}
            type={FIELD_TYPE.TEXTAREA}
            label={{ text: translation.folder_title, required: true }}
            serialize={{ type: SerializeType.PRIMITIVE_VALUE }}
            initialValue={definition.favourite.d} />
        </div>
        <div className={`${styles.createFolder} col-lg-4`}>
          <SimpleForm
            name={formFolder.folder}
            type={FIELD_TYPE.SELECT_SINGLE_HIERARCHY}
            optionsEndpoint={endpoint.loadFolderOptions}
            optionsParams={{ folderType, sharedWithCurrentUser: false }}
            label={{ text: translation.folder }}
            initialValue={validState.parentFavourite ? codeItemToStoreValue(validState.parentFavourite) : ''}
            translation={translation}
            placeholder={translation.search_placeholder}
            serialize={{ type: SerializeType.V_VALUE }} />
          <CreateFolder
            {...rest}
            translation={translation}
            fetching={fetchingCreateFolder}
            selectedReport={selectedReport} />
        </div>
        <div className="col-lg-4">
          <SimpleForm
            name={formFolder.owners}
            className={styles.noMargin}
            type={FIELD_TYPE.SELECT_MULTIPLE_HIERARCHY}
            optionsEndpoint={endpoint.loadOwnerOptions}
            label={{ text: translation.owner }}
            initialValue={validState.owners ? validState.owners.map(codeItemToStoreValue) : undefined}
            translation={translation}
            placeholder={translation.search_placeholder}
            serialize={{ type: SerializeType.PRIMITIVE_VALUE }}
            displaySelectAll={true} />
          {hasDescendants && this.descendantsOwnerNotUpdated() &&
            <Validation level={ValidationLevel.error}
              messages={[
                {
                  level: ValidationLevel.error,
                  message: translation.error_message_descendants_owner_not_updated
                }]} />}
          {hasParent && this.parentOwnerNotUpdated() &&
            <Validation level={ValidationLevel.error}
              messages={[
                {
                  level: ValidationLevel.error,
                  message: translation.error_message_parent_owner_not_updated
                }]} />}
          <SimpleForm
            name={formFolder.updateOwner}
            className={styles.noMargin}
            type={FIELD_TYPE.CHECKBOX}
            label={{ text: textUpdateOwners }}
            infoText={translation.update_owners_info}
            initialValue={false}
            translation={translation}
            placeholder={translation.search_placeholder}
            serialize={{ type: SerializeType.PRIMITIVE_VALUE }} />
        </div>
      </div>

      <h3>{translation.share_with}</h3>
      <hr />

      <div className="row">
        <div className="col-lg-4">
          <SimpleForm
            name={formFolder.user_group}
            type={FIELD_TYPE.SELECT_MULTIPLE_HIERARCHY}
            optionsEndpoint={endpoint.loadUserGroupsOptions}
            label={{ text: translation.user_group }}
            initialValue={validState.sharedWithUserGroups ? validState.sharedWithUserGroups.map(codeItemToStoreValue) : ''}
            translation={translation}
            placeholder={translation.search_placeholder}
            serialize={{ type: SerializeType.PRIMITIVE_VALUE }}
            displaySelectAll={true} />
        </div>
        <div className="col-lg-4">
          <SimpleForm
            name={formFolder.users}
            type={FIELD_TYPE.SELECT_MULTIPLE_HIERARCHY}
            optionsEndpoint={endpoint.loadUsersOptions}
            label={{ text: translation.users }}
            initialValue={validState.sharedWithUsers ? validState.sharedWithUsers.map(codeItemToStoreValue) : ''}
            translation={translation}
            placeholder={translation.search_placeholder}
            serialize={{ type: SerializeType.PRIMITIVE_VALUE }}
            searchRequired={searchRequiredUsers} />
        </div>
        <div className="col-lg-4">
          <SimpleForm
            name={formFolder.updateSharing}
            type={FIELD_TYPE.CHECKBOX}
            label={{ text: translation.share_contents }}
            initialValue={false}
            translation={translation}
            placeholder={translation.search_placeholder}
            serialize={{ type: SerializeType.PRIMITIVE_VALUE }}
            infoText={translation.copied_to_all_reports_in_current_folder} />
        </div>
      </div>
    </div>;
  };

  public render() {
    const { translation, fetchingFolder, definition, formValues, hasDescendants } = this.props;
    const hasParent = !!definition?.validState?.parentFavourite;
    const canSave = !(fetchingFolder || (hasDescendants && this.descendantsOwnerNotUpdated()) || 
      (hasParent && this.parentOwnerNotUpdated()));

    return <ModalWrapper
      headerTitle={translation.edit_folder}
      btnTitle={translation.save_folder}
      onDone={this.onSubmit}
      isDisabled={!canSave}
      btnCancelTitle={translation.cancel}
      closeOnDone={!!(formValues[form.folder])}
      onClose={this.onCancel}
      size={ModalSize.large}
    >
      {!definition ? <ElementLoader /> : this.renderFields()}
    </ModalWrapper>;
  }
}
const container = withSimpleForm<EditFolderProps>(EditFolderComponent);

export { container as EditFolder };
